/**
 * ViewPieMenu — backtick (`) opens a Blender-style pie for axis-view snapping.
 *
 * Six canonical axis views (Front/Back/Left/Right/Top/Bottom) plus a seventh
 * "Perspective" option that exits the lock, matching Blender's grave-accent
 * pie. The tool-palette `ViewAxisControl` belt is retained alongside for
 * touch users (see ToolPalette.tsx).
 */

import { useCallback, useMemo } from "react";
import { useStore } from "../app/store.ts";
import type { ViewAxis } from "../app/storeTypes.ts";
import {
  exitAxisView,
  snapToAxisView,
  VIEW_AXIS_OPTIONS,
} from "../viewport/cameraViews.ts";
import { useOperatorMenu } from "./hooks/useOperatorMenu.ts";
import { PieMenu, type PieMenuOption } from "./PieMenu.tsx";
import { COLOR_AXIS_X, COLOR_AXIS_Y, COLOR_AXIS_Z } from "./tokens.ts";

const PERSPECTIVE_ID = "perspective";

const AXIS_COLOR: Record<ViewAxis, string> = {
  x: COLOR_AXIS_X,
  y: COLOR_AXIS_Y,
  z: COLOR_AXIS_Z,
};

/** Two-character badge for an axis+sign, e.g. "-Y" for front, "+X" for right. */
function AxisBadge({ axis, sign }: { axis: ViewAxis; sign: 1 | -1 }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontWeight: 700,
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: -0.5,
        color: AXIS_COLOR[axis],
      }}
    >
      {sign < 0 ? "\u2212" : "+"}
      {axis.toUpperCase()}
    </span>
  );
}

export function ViewPieMenu() {
  const menu = useOperatorMenu("viewPie");
  const viewLock = useStore((s) => s.viewLock);

  const options: PieMenuOption[] = useMemo(() => {
    const axes = VIEW_AXIS_OPTIONS.map<PieMenuOption>((opt) => ({
      id: `${opt.axis}${opt.sign}`,
      label: opt.label,
      icon: <AxisBadge axis={opt.axis} sign={opt.sign} />,
    }));
    axes.push({
      id: PERSPECTIVE_ID,
      label: "Perspective",
      disabled: viewLock == null,
    });
    return axes;
  }, [viewLock]);

  const handleSelect = useCallback(
    (id: string) => {
      if (id === PERSPECTIVE_ID) {
        // Only way out of a lock from the pie — picking an axis view never
        // toggles off (re-selecting the active axis just re-centers it).
        exitAxisView();
      } else {
        const opt = VIEW_AXIS_OPTIONS.find((o) => `${o.axis}${o.sign}` === id);
        if (opt) snapToAxisView(opt.axis, opt.sign);
      }
      menu.close();
    },
    [menu],
  );

  return (
    <PieMenu
      open={menu.open}
      onClose={menu.close}
      onSelect={handleSelect}
      anchorPoint={menu.anchorPoint}
      options={options}
      holdKey="`"
      holdCode="Backquote"
    />
  );
}
